import React from 'react';
import { api } from '@/App';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'Rp.\u00a00';
  const hasFraction = Number(amount) % 1 !== 0;
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: hasFraction ? 2 : 0
  }).format(amount).replace('Rp', 'Rp.');
};

const InventoryDialog = ({ 
  open, 
  onClose, 
  inventoryDetails,
  balanceSheet,
  searchFilter,
  onSearchChange,
  selectedPeriod,
  onImportStokClick
}) => {
  if (!open) return null;

  const filteredInventory = inventoryDetails?.filter(inv => 
    inv.branchName.toLowerCase().includes((searchFilter || '').toLowerCase())
  ) || [];

  const handleImportStokClick = async (inv) => {
    try {
      const periodId = selectedPeriod;
      const transfersRes = await api.get('/period-transfers');
      const importStokTransfers = transfersRes.data.filter(t => 
        t.transfer_type === 'IMPORT_STOCK' && 
        t.source_period_id === periodId &&
        (t.branch_id === inv.branchId || !t.branch_id)
      );
      
      if (importStokTransfers.length > 0) {
        onImportStokClick({
          branchId: inv.branchId,
          branchName: inv.branchName,
          transfers: importStokTransfers
        });
      } else {
        toast.info('Tidak ada Import Stok untuk cabang ini');
      }
    } catch (error) {
      console.error('Failed to fetch Import Stok:', error);
      toast.error('Gagal memuat Import Stok');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      data-testid="inventory-dialog-overlay"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden" 
        onClick={(e) => e.stopPropagation()}
        data-testid="inventory-dialog"
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Detail Persediaan per Cabang</h2>
              <p className="text-sm text-slate-600 mt-1">
                Total: {formatCurrency(balanceSheet?.inventory || 0)} dari {inventoryDetails?.length || 0} cabang
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
              data-testid="inventory-dialog-close"
            >
              ×
            </button>
          </div>
          <div className="mt-4">
            <input
              type="text"
              placeholder="Cari nama cabang..."
              value={searchFilter || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="inventory-search-input"
            />
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <table className="w-full" data-testid="inventory-table">
            <thead className="bg-purple-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Nama Cabang</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Berat (gr)</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Import Stok</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Jumlah Item</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((inv, idx) => (
                <tr key={inv.branchId} className={idx % 2 === 0 ? 'bg-white' : 'bg-purple-50'}>
                  <td className="px-4 py-3 text-slate-800 font-medium">{inv.branchName}</td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {inv.inventoryWeight.toFixed(2)} gr
                  </td>
                  <td 
                    className="px-4 py-3 text-right text-purple-600 font-semibold cursor-pointer hover:bg-purple-100"
                    onClick={() => handleImportStokClick(inv)}
                    data-testid={`inventory-import-stok-${inv.branchId}`}
                  >
                    {formatCurrency(inv.inventoryAmount)}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-700">
                    {inv.itemCount} item
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-purple-100">
              <tr>
                <td className="px-4 py-3 text-left font-bold text-slate-800">Total</td>
                <td className="px-4 py-3 text-right font-bold text-slate-700">
                  {(inventoryDetails?.reduce((sum, inv) => sum + inv.inventoryWeight, 0) || 0).toFixed(2)} gr
                </td>
                <td className="px-4 py-3 text-right font-bold text-purple-600">
                  {formatCurrency(inventoryDetails?.reduce((sum, inv) => sum + inv.inventoryAmount, 0) || 0)}
                </td>
                <td className="px-4 py-3 text-center font-bold text-slate-700">
                  {inventoryDetails?.reduce((sum, inv) => sum + inv.itemCount, 0) || 0} item
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        
        <div className="p-4 border-t flex justify-end">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            data-testid="inventory-dialog-close-btn"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryDialog;
